import { useCallback, useMemo, useState } from 'react';
import {
    createRequest,
    type AnyVariables,
    type DocumentInput,
    type UseQueryArgs,
    type UseQueryState,
} from 'urql';
import { useQuery } from './use-query.ts';

/**
 * Relay-style query input variables to control paging.
 */
export type PagedQueryVariables = {
    after?: string;
    first?: number;
};

/**
 * Represents a Relay-style paged result (a _connection_ in Relay terms).
 */
export type PagedResult<Node> = {
    nodes: readonly Node[];
    pageInfo: {
        hasNextPage: boolean;
        endCursor?: string | null;
    };
};

/**
 * Helper type to return the node type for a given {@link PagedResult} type.
 */
export type NodeTypeOf<T extends PagedResult<unknown>> = T extends PagedResult<infer Node> ? Node : never;

/**
 * Input to {@link useInfiniteQuery}.
 */
export type UseInfiniteQueryArgs<Data, Node, Variables extends AnyVariables> = Omit<
    UseQueryArgs<Variables & PagedQueryVariables, Data>,
    'query' | 'variables'
> & {
    /** A GraphQL query which includes `after` and `first` variables. */
    query: DocumentInput<Data, Variables & PagedQueryVariables>;
    /** The query input, excluding `after` and `first` */
    variables: Variables;
    /** Selects the source of the paged data within the result. */
    getConnection: (data: Data) => PagedResult<Node>;
    /** The size of pages to load. */
    pageSize?: number;
};

/**
 * Returned from {@link useInfiniteQuery}.
 */
export type UseInfiniteQueryResponse<Data, Node, Variables extends AnyVariables> = {
    /** The urql state of the underlying query. */
    state: UseQueryState<Data, Variables & PagedQueryVariables>;
    /** A list of nodes, flattened from all loaded pages. */
    nodes: readonly Node[];
    /** True if the last result indicated more data is available. */
    hasNextPage: boolean;
    /** True if there is no data loaded. */
    isPending: boolean;
    /** True if there is no data loaded yet and we are currently fetching. */
    isLoading: boolean;
    /** True if we are currently fetching the next chunk of data. */
    isFetchingNextPage: boolean;
    /** Trigger the fetch of the next page of data. */
    fetchNextPage: () => void;
};

/**
 * Wraps urql's {@link useQuery} to provide a react-query style infinite query
 * hook. The supplied query must include a connection shape which is selected by
 * `getConnection`. This hook automatically deals with caching previous pages
 * and fetching next pages when `fetchNextPage` is called.
 */
export function useInfiniteQuery<Data, Node, Variables extends AnyVariables = AnyVariables>(
    args: UseInfiniteQueryArgs<Data, Node, Variables>,
): UseInfiniteQueryResponse<Data, Node, Variables> {
    const { query, variables, getConnection, pageSize, ...rest } = args;
    const baseKey = useMemo(() => createRequest(query, variables).key, [query, variables]);

    const [cursor, setCursor] = useState<string | undefined>(undefined);
    const [pages, setPages] = useState<Data[]>([]);
    const [lastBaseKey, setLastBaseKey] = useState(baseKey);
    const [committedKey, setCommittedKey] = useState<string | null>(null);

    if (baseKey !== lastBaseKey) {
        setLastBaseKey(baseKey);
        setCursor(undefined);
        setPages([]);
        setCommittedKey(null);
    }

    const [state] = useQuery({
        ...rest,
        query,
        variables: {
            ...variables,
            ...(pageSize !== undefined && { first: pageSize }),
            after: cursor,
        } as Variables & PagedQueryVariables,
    });

    const currentKey = `${baseKey}:${cursor ?? ''}`;
    if (state.data && !state.fetching && committedKey !== currentKey) {
        setCommittedKey(currentKey);
        const data = state.data;
        setPages((prev) => (cursor === undefined ? [data] : [...prev, data]));
    }

    const connections = useMemo(() => pages.map(getConnection), [pages, getConnection]);
    const nodes = useMemo(() => connections.flatMap((c) => c.nodes), [connections]);

    const lastPageInfo = connections.at(-1)?.pageInfo;
    const hasNextPage = lastPageInfo?.hasNextPage ?? false;
    const nextCursor = hasNextPage ? (lastPageInfo?.endCursor ?? undefined) : undefined;

    const fetchNextPage = useCallback(() => {
        if (!hasNextPage || state.fetching) {
            return;
        }
        setCursor(nextCursor);
    }, [hasNextPage, nextCursor, state.fetching]);

    const isPending = pages.length === 0;

    return {
        state,
        nodes,
        hasNextPage,
        isPending,
        isLoading: isPending && state.fetching,
        isFetchingNextPage: state.fetching && cursor !== undefined,
        fetchNextPage,
    };
}
